import React from 'react'

import { MerchantLogo } from '@mxenabled/mxui'

import { useTheme } from '@mui/material/styles'

import { BarDataProps } from '../../../../types/Charts/BarChart'
import { stripChartSlotProps } from '../../../../../common/components/charts/chartSlotProps'

interface BarStyleProps extends React.CSSProperties {
  x?: number
  y?: number
  width?: number
  height?: number
}
export interface CustomBarPlotProps {
  colorRange: string[]
  data: BarDataProps[]
  dataIndex: number
  removeSelectedTabColorChange?: boolean
  selectedTabIndex: number
  style: BarStyleProps
}

export const CustomBarPlot = ({
  colorRange,
  data,
  dataIndex,
  removeSelectedTabColorChange,
  selectedTabIndex,
  style,
  ...restProps
}: CustomBarPlotProps) => {
  const theme = useTheme()
  const rectRef = React.useRef<SVGRectElement>(null)

  React.useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        if (rectRef.current) {
          const { width } = rectRef.current.getBoundingClientRect()

          const xValue = width / 2
          const textElement = rectRef.current.querySelector('text')
          if (textElement) {
            textElement.setAttribute('transform', `translate(${xValue}, -10)`)
          }
        }
      })
    })

    if (rectRef.current) resizeObserver.observe(rectRef.current)
    return () => {
      if (rectRef.current) resizeObserver.unobserve(rectRef.current)
    }
  }, [])

  const { ariaLabel, category, categoryGuid, formattedAmount, imageSize, merchantGuid } =
    data[dataIndex]

  const shouldAmountAmountTextBeBold = selectedTabIndex === dataIndex ? 'bold' : 400

  const minY = 40
  const y = style.y ?? 0

  if (y < minY) {
    const diff = minY - y

    style.y = minY
    style.height = Math.max(0, (style.height ?? 0) - diff)
  }

  return (
    <g aria-label={ariaLabel}>
      <rect
        {...stripChartSlotProps(restProps)}
        fill={colorRange[dataIndex]}
        ref={rectRef}
        rx={2}
        style={style}
      />
      {(merchantGuid || categoryGuid) && (
        <foreignObject
          height={imageSize || 32}
          width={imageSize || 32}
          x={
            typeof style.x === 'number' && typeof style.width === 'number'
              ? style.x + style.width / 2 - (imageSize || 32) / 2
              : 0
          }
          y={
            typeof style.y === 'number' && typeof style.height === 'number'
              ? style.y + style.height / 2 - (imageSize || 32) / 2
              : 0
          }
        >
          <MerchantLogo
            alt={category}
            categoryGuid={categoryGuid || ''}
            merchantGuid={merchantGuid || ''}
            size={imageSize || 32}
          />
        </foreignObject>
      )}
      <text
        fill={theme.palette.text.primary}
        fontSize={11}
        fontWeight={removeSelectedTabColorChange ? 'bold' : shouldAmountAmountTextBeBold}
        textAnchor="middle"
        x={
          typeof style.x === 'number' && typeof style.width === 'number'
            ? style.x + style.width / 2
            : 0
        }
        y={typeof style.y === 'number' ? style.y - (category ? 26 : 14) : 0}
      >
        {formattedAmount}
      </text>
      {category && (
        <text
          fill={theme.palette.text.secondary}
          fontSize={9}
          fontWeight={
            selectedTabIndex === dataIndex && !removeSelectedTabColorChange ? 'bold' : 400
          }
          textAnchor="middle"
          x={
            typeof style.x === 'number' && typeof style.width === 'number'
              ? style.x + style.width / 2
              : 0
          }
          y={typeof style.y === 'number' ? style.y - 14 : 0}
        >
          {category}
        </text>
      )}
    </g>
  )
}
